/**
 * 将看房行程添加到日历
 * 
 * @Author: Focci
 * @Date: 2023-08-16 22:36:04
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-16 22:36:04
 */

'use client'

import AspectRatioCover from '@comp/AspectRatioCover'
import Chips from '@page/common/card/property/Chips'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import PropertyIcons from '@page/common/card/property/PropertyIcons'
import Link from 'next/link'
import { isAU } from '@lib/utils'
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react'
import { propertyDetail } from '@lib/route/house'
import { AddToCalendarButton } from 'add-to-calendar-button-react'
import { timeZone } from '@lib/config'
import { useLocale, useTranslations } from 'next-intl'
import { Divider } from '@comp/material'
import { map, isArray, isFunction } from 'lodash-es'
import { formatDate } from '@lib/datetime'
import { myAppointments } from '@lib/route/profile'
import SendEmailOption from './SendEmailOption'

export function PropertyCardOfCalendar({ house }) {
  return (
    <div className="w-full md:w-[18.75rem] relative shrink-0">
      <AspectRatioCover
        width={750}
        height={1000}
        src={house.cover}
        alt={house.h1}
      />
      <div className="absolute z-1 left-2 top-2">
        <Chips 
          openHomeLabel={house.openHomeLabel}
          newListing={house.newListing}
          newHomes={house.newHomes}
        />
      </div>
      <div
        style={{ background: 'linear-gradient(0deg, black, transparent)' }}
        className="
          absolute inset-0 top-auto px-4 pb-4 pt-8 text-white flex 
          flex-col gap-0.5 text-f.8 font-medium
        "
      >
        <div>{house.priceLabel}</div>
        <div>{house.street}</div>
        <PropertyIcons 
          iconWidth="1.1rem"
          className="gap-x-4" 
          data={house} 
        />
      </div>
    </div>
  )
}

function getCalendarOptions(data, house, isAuction, th, ta, locale) {
  const agentLabel = map(
    isArray(house.agents) ? house.agents : [],
    (agent) => `${agent.first_name} ${agent.last_name} (tel: ${agent.phone_numbers?.mobile})\\n`,
  )
  const name = [
    house.suburbName,
    `${ta('bedroomsCount', { count: house.bedrooms, suffix: '' })}`,
    !isAuction ? th('openhome') : th('auction')
  ].join(' ')
  
  const description = [
    `${house.street}, ${house.suburbName}`,
    `${th('bedroomCount')}: ${house.bedrooms}`,
    `${th('priceBold')}: ${house.priceBold}\n`,
    `${agentLabel.join('\n')}\n`,
    `${propertyDetail(house.slug, locale)}`,
  ].join('\n')

  const tmp = {
    name,
    description,
    location: house.address || house.street,
    timeZone: isAU ? 'currentBrowser' : timeZone
  }

  if(isAuction) {
    tmp.startDate = formatDate(data.time_start, 'YYYY-MM-DD')
    tmp.startTime = formatDate(data.time_start, 'HH:mm')
    tmp.endTime = formatDate(data.time_start, 'HH:mm')
  } else {
    tmp.startDate = formatDate(data.time_start, 'YYYY-MM-DD')
    tmp.startTime = formatDate(data.time_start, 'HH:mm')
    tmp.endDate = formatDate(data.time_end, 'YYYY-MM-DD')
    tmp.endTime = formatDate(data.time_end, 'HH:mm')
  }

  return tmp
}

function AddCalendarReminder({
  auction = false,
  data = {},
  house = {}
}, ref) {
  const refSendEmail = useRef()
  const locale = useLocale()
  const th = useTranslations('house')
  const ta = useTranslations('agent')
  const calendarOptions = getCalendarOptions(data, house, auction, th, ta, locale)
  const url = `${myAppointments(locale)}?date=${formatDate(data.time_start, 'YYYY-MM-DD')}`

  const title = useMemo(() => {
    const res = th('viewingsItineraryIsAdded')
    return res.replace(
      '[openhomeOrAuction]',
      auction ? th('auction') : th('openhome')
    )
  }, [auction, th])

  useImperativeHandle(ref, () => ({
    sendEmailToAgent: () => {
      const { sendEnquireEmail } = refSendEmail.current
      isFunction(sendEnquireEmail) && sendEnquireEmail()
    }
  }))
  
  return (
    <>
      <div className="flex flex-col md:flex-row gap-4 -m-4 border-b border-b-[20px] border-b-cf8">
        <PropertyCardOfCalendar house={house} />
        <div className="flex-auto p-6">
          <h3 className="text-f1.2 font-bold">
            {title}
          </h3>
          <Link href={url} target="_blank" className="mt-2 text-primary inline-flex items-center">
            {th('checkOutViewingsItinerary')}
            <ArrowForwardIosIcon />
          </Link>
          <Divider className="my-6" />
          <div>
            <b className="text-f.8">
              {th('addItineraryToCalendar')}
            </b>
            <div className="mt-4">
              <AddToCalendarButton
                buttonsList
                hideBackground
                buttonStyle="custom"
                customCss="/css/atcb.css"
                options="'Apple','Google','iCal','Outlook.com','Microsoft 365','Microsoft Teams','Yahoo'"
                lightMode="bodyScheme"
                {...calendarOptions}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-8">
        <SendEmailOption 
          house={house}
          data={data}
          ref={refSendEmail} 
          type={auction ? '3' : '2'}
        />
      </div>
    </>
  )
}

export default forwardRef(AddCalendarReminder)
